import { useMutation } from "@apollo/client";
import CheckIcon from "@mui/icons-material/Check";
import { Button, Grid, Typography, useTheme } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { getFullName } from "encamp-shared/src/utils/name";
import { gql } from "generated-graphql";
import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";

import { Reviewer } from "./ReviewForm";

export const SUBMIT_REVIEW = gql(`
  mutation SetReviewComplete($reportId: ID!, $reviewComplete: Boolean!) {
    setReviewComplete(id: $reportId, reviewComplete: $reviewComplete) {
      id
      organizationStatus
      notesToRegulators
      touchedSteps
      reviewers {
        id
        assignmentType
        isComplete
        person {
          id
          first
          last
          phones {
            number
            type
          }
        }
      }
    }
  }
`);

export function SubmitReviewButtons({ reviewer }: { reviewer?: Reviewer }) {
  const theme = useTheme();
  const alerts = useAlerts();
  const { reportId = "" } = useParams<{ reportId: string }>();
  const [showReviewReport, setShowReviewReport] = useState(false);

  const [setReviewComplete, { loading: submittingReview }] = useMutation(SUBMIT_REVIEW, {
    update(cache, { data }) {
      const reportData = data?.setReviewComplete;
      if (!reportData) {
        return;
      }
      const updatedReviewer = reportData.reviewers.find((r) => r.person.id === reviewer?.id);

      if (updatedReviewer) {
        cache.modify({
          id: cache.identify({
            __typename: "TierIIReport",
            id: reportData.id,
          }),
          fields: {
            reviewers: (existingRefs = [], { readField }) => {
              return existingRefs?.map((ref: any) => {
                if (readField("id", ref) === updatedReviewer?.id) {
                  return {
                    ...ref,
                    isComplete: updatedReviewer.isComplete,
                  };
                }
                return ref;
              });
            },
          },
        });
      }
    },
  });

  const handleSubmitReview = useCallback(
    async (reviewComplete: boolean) => {
      try {
        await setReviewComplete({
          variables: {
            reportId,
            reviewComplete,
          },
        });
        alerts.success(reviewComplete ? "Review submitted successfully" : "Review removed");
      } catch (error) {
        alerts.error("There was a problem submitting your review");
      }
      setShowReviewReport(false);
    },
    [reportId, alerts, setReviewComplete]
  );

  return (
    <>
      <Grid container justifyContent="end">
        {reviewer?.isComplete ? (
          <Button
            variant="text"
            color="success"
            sx={{ width: "auto", cursor: "default", pointerEvents: "none" }}
            startIcon={<CheckIcon />}
          >
            Review submitted
          </Button>
        ) : (
          <Button
            variant="contained"
            size="large"
            onClick={() => setShowReviewReport(true)}
            disabled={reviewer?.isComplete ?? false}
            sx={{ width: "auto" }}
          >
            Submit Review
          </Button>
        )}
      </Grid>
      <ConfirmDialog
        open={showReviewReport}
        onClose={() => setShowReviewReport(false)}
        header="Are you sure you want to submit this review?"
        confirmText="Yes, I have reviewed this report"
        loading={submittingReview}
        actions={
          <>
            <Button disabled={submittingReview} onClick={() => setShowReviewReport(false)}>
              Cancel
            </Button>
            <Button
              loading={submittingReview}
              disabled={submittingReview}
              onClick={() => handleSubmitReview(true)}
              variant="contained"
            >
              {submittingReview ? "Loading..." : "Yes, I have reviewed this report"}
            </Button>
          </>
        }
        onConfirm={() => handleSubmitReview(true)}
        info={
          <>
            <Typography sx={{ marginBottom: theme.spacing(1) }}>
              I, {getFullName(reviewer?.person)}, confirm that I have reviewed this report on{" "}
              {new Date().toLocaleDateString()} for correctness and completeness and that it is
              ready for verification and submission to Encamp.
            </Typography>
            <Typography sx={{ fontStyle: "italic" }}>
              The report assignee will be notified of this review via email.
            </Typography>
          </>
        }
      />
    </>
  );
}
